/**
 * Polls the maintenance probe while the maintenance page is shown (DEV-6343).
 *
 * Cadence comes from the backend (Vault-configured, default 5 min). The probe goes through the shared
 * axios instance, which injects {@code X-Tenant-ID} from {@code window.parentContext.tenantId}.
 *
 * On recovery (probe reports maintenance=false), the page is reloaded at the home page — the user
 * lands on the standard portal home, as if they had just arrived. A full reload is used on purpose:
 * while in maintenance the bootstrap handshake is short-circuited before {@code extract-context}, so the
 * parent context was never extracted; reloading re-runs the handshake and restores a working session.
 */

import { useEffect } from 'react';
import { useUIStore } from '../store/uiStore';
import { getMaintenanceStatus } from '../services/api/endpoints/maintenanceEndpoints';

export function useMaintenancePolling(): void {
  const pollIntervalMs = useUIStore((state) => state.maintenancePollIntervalMs);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      try {
        const status = await getMaintenanceStatus();
        if (cancelled) {
          return;
        }
        if (!status.maintenance) {
          // Recovery — reload at the home page to re-run the parent handshake; the user lands on the
          // standard portal home, as if they had just arrived.
          window.location.assign('/');
        } else {
          // Keep the cadence in sync with the (possibly updated) backend value.
          useUIStore.getState().setMaintenance(true, status.pollIntervalMs);
        }
      } catch {
        // Probe failed (still down / transient) — keep polling.
      }
    };

    // Probe immediately on mount, then on the configured cadence.
    void check();
    const intervalId = window.setInterval(() => void check(), pollIntervalMs);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [pollIntervalMs]);
}
